import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * A custom element that allows the user to clean a search input.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchPageInput - The search input element.
 * @extends {Component<Refs>}
 */
const buildTitleAndQuery = (query) => {
  const terms = query.trim().split(/\s+/).filter(Boolean);

  return terms
    .map((term) => {
      const escapedTerm = term
        .replace(/\\/g, '\\\\')
        .replace(/"/g, '\\"')
        .replace(/:/g, '\\:')
        .replace(/\(/g, '\\(')
        .replace(/\)/g, '\\)');

      return `title:"${escapedTerm}"`;
    })
    .join(' AND ');
};

class SearchPageInputComponent extends Component {
  requiredRefs = ['searchPageInput'];

  /**
   * Handles the keydown event on the search input and resets the search when
   * empty and Escape is pressed.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown = debounce((event) => {
    const value = this.refs.searchPageInput.value.trim();

    if (event.key === 'Escape' && value === '') {
      this.#submitEmptySearch();
    }
  }, 100);

  connectedCallback() {
    super.connectedCallback();

    const params = new URLSearchParams(window.location.search);
    const displayQuery = params.get('display_q');
    const noResultsDisplayQuery = this.refs.searchPageInput.form?.querySelector('[data-no-results-display-query]');

    if (displayQuery) {
      this.refs.searchPageInput.value = displayQuery;
    }

    if (displayQuery && noResultsDisplayQuery) {
      noResultsDisplayQuery.textContent = displayQuery;
    }

    this.refs.searchPageInput.form?.addEventListener('submit', this.#handleSubmit);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.searchPageInput.form?.removeEventListener('submit', this.#handleSubmit);
  }

  #handleSubmit = () => {
    const searchInput = this.refs.searchPageInput;
    const query = searchInput.value.trim();
    const displayQueryInput = searchInput.form?.querySelector('[data-display-query]');
    const technicalQueryInput = searchInput.form?.querySelector('[data-technical-search-query]');

    if (!query) return;

    if (displayQueryInput) {
      displayQueryInput.value = query;
    }

    if (technicalQueryInput) {
      technicalQueryInput.value = buildTitleAndQuery(query);
    }
  };

  #submitEmptySearch() {
    const searchInput = this.refs.searchPageInput;

    searchInput.focus();
    searchInput.value = '';

    if (this.#isEmptyState()) return;

    searchInput.form?.submit();
  }

  #isEmptyState = () => {
    const url = new URL(window.location.href);
    const queryParam = url.searchParams.get('q') ?? '';

    return queryParam.trim() === '';
  };
}

if (!customElements.get('search-page-input-component')) {
  customElements.define('search-page-input-component', SearchPageInputComponent);
}
